import { ReactNode, useEffect, useRef, useState } from 'react';
import { P2GBox } from '@parcel2go/core-ui';
import Loading from '~/components/loading/Loading';

/**
 * How long to keep the loader up after the frame's document has loaded but before checkout has said
 * anything. A frame checkout does not recognise as embedded (a disabled tenant, a host origin that
 * is not allow-listed) posts NOTHING, so waiting on a message alone would spin forever. Bounded,
 * never a poll.
 */
const READY_FALLBACK_MS = 3000;

/**
 * Cross-fade duration. The loader stays mounted for this long after the reveal so the two overlap
 * rather than leaving a blank gap - the frame fades in while the spinner fades out.
 */
const REVEAL_MS = 250;

/** How long after a shrink the frame's previous height still counts as where the customer was looking. */
const SHRINK_WINDOW_MS = 1000;

/** jsdom implements no matchMedia, and this is not worth a guard clause at every call site's tests. */
function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Someone who has asked their OS for less motion gets the swap instantly instead of a fade. */
function revealDuration(): number {
  return prefersReducedMotion() ? 0 : REVEAL_MS;
}

/** The namespace every message ui-checkout posts carries. */
export const CHECKOUT_MESSAGE_SOURCE = 'p2g-checkout';

/** A message from the framed checkout. `type` is the discriminator; the rest is per-message. */
export interface CheckoutHostMessage {
  source: typeof CHECKOUT_MESSAGE_SOURCE;
  type: string;
  [key: string]: unknown;
}

interface props {
  /** The checkout route to frame, with no query string of its own - params go in `params`. */
  path: string;
  /** Route-specific query params (`tier`/`interval`/`plan`, an order `id`/`hash`, ...). */
  params?: Record<string, string>;
  /** The iframe's accessible name. */
  title: string;
  /**
   * Bump to re-load the same URL in place, e.g. after recovering a lapsed session. It rides on the
   * query string deliberately: remounting the element with a React `key` would leave the listener
   * holding a detached frame, and every subsequent message would fail the `event.source` check.
   */
  reloadNonce?: number;
  /**
   * Every message except `checkout:resize` and `subscription:step`, which this component handles itself. Need not be
   * stable - it is read through a ref, so changing it never re-registers the listener.
   */
  onMessage?: (message: CheckoutHostMessage) => void;
  /** Height to hold before checkout reports its own - also the space the loader sits in. */
  minHeight?: string;
  children?: ReactNode;
}

function buildSrc(path: string, params: Record<string, string>, reloadNonce: number): string {
  const search = new URLSearchParams({
    // Arms the bridge and drops checkout's chrome - its header, footer, grey canvas, page
    // background and gutter. Without it checkout navigates itself inside the frame, paints white
    // over our panel, and posts us nothing.
    headless: 'true',
    // Ignored in production (same-origin); required for the allow-listed cross-origin dev workflow.
    hostOrigin: window.location.origin,
    ...params
  });
  // Host-local cache-buster, ignored by checkout.
  if (reloadNonce > 0) {
    search.set('_r', String(reloadNonce));
  }
  return `${path}?${search.toString()}`;
}

/**
 * Frames a ui-checkout route and speaks its `postMessage` host bridge
 * (ui-checkout `docs/EMBEDDED_PAYMENT.md`). Owns the three things every embed of it needs and must
 * not get subtly wrong twice: the URL flags that arm the bridge, the three-check listener, and
 * sizing the frame from `checkout:resize`.
 *
 * It deliberately knows nothing about any particular route's messages - a caller switches on
 * `onMessage` - with ONE exception: `subscription:step`. Both subscription surfaces (the plans page
 * My Plan frames, and the single-plan checkout) post it, and bringing the frame into view needs the
 * frame's own element and scroll history, which only this component has. It is handled here and never
 * forwarded to `onMessage`, so a caller must not wait for it there. Everything else stays with the
 * caller, which keeps the security-critical part in one place while the plans page (which only
 * needs resizing) and the subscription checkout (which has a five-message conversation) stay
 * separate.
 */
const CheckoutFrame = ({ path, params, title, reloadNonce = 0, onMessage, minHeight = '70vh', children }: props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  // Read inside the message handler, which is registered once per `path` and so cannot see later
  // `height` state. They let a `subscription:step` tell how tall the frame WAS, because its resize can
  // land before or after the step.
  const heightRef = useRef<number | null>(null);
  const lastShrinkRef = useRef<{ from: number; at: number } | null>(null);
  // An iframe paints blank while its document loads, and again while the app inside it boots. Both
  // are our blank space to own, so hold a loader over the frame until checkout is actually up.
  const [revealed, setRevealed] = useState(false);
  // Tracked separately from `revealed` so the loader can fade OUT across the same window the frame
  // fades in, instead of vanishing the instant the reveal starts and leaving a blank beat.
  const [loaderMounted, setLoaderMounted] = useState(true);

  // Read through a ref so the listener is registered exactly once per frame. Depending on the
  // callback instead would re-register on every render of a caller that forgot to memoise it.
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  // The iframe element stays mounted when `path` or `reloadNonce` changes, but its DOCUMENT is a new one,
  // so what the old one reported must not leak into the new one's steps.
  useEffect(() => {
    heightRef.current = null;
    lastShrinkRef.current = null;
  }, [path, reloadNonce]);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame) return;

    const checkoutOrigin = new URL(path, window.location.href).origin;

    function handleMessage(event: MessageEvent) {
      // Three checks, in this order, per the host-bridge contract. `event.source` is the one that
      // actually proves the message came from checkout: in production the host and checkout share
      // an origin, so any other same-origin window could otherwise forge one. `event.origin` is
      // compared against CHECKOUT's origin (derived from the framed URL), not ours, so the
      // cross-origin dev workflow is not silently dropped. The namespace is third because it is
      // forgeable on its own.
      if (event.source !== frame?.contentWindow) return;
      if (event.origin !== checkoutOrigin) return;
      if (!event.data || event.data.source !== CHECKOUT_MESSAGE_SOURCE) return;

      // Anything checkout says proves it has booted. `checkout:ready` is the message meant for
      // this, but a route may never post it (the plans route doesn't) and may post nothing beyond
      // `checkout:resize` (the app-wide bridge) until a customer actually completes something - so
      // the first message of any kind is the reveal.
      setRevealed(true);

      if (event.data.type === 'checkout:resize') {
        const newHeight = event.data.height as number;
        if (heightRef.current !== null && newHeight < heightRef.current) {
          lastShrinkRef.current = { from: heightRef.current, at: Date.now() };
        }
        heightRef.current = newHeight;
        setHeight(newHeight);
        return;
      }

      // The checkout says a screen the customer must see has just arrived (a payment form giving way
      // to the activation spinner, say). The frame's top edge never moves when it resizes, so if it is
      // scrolled past, the customer cannot see where the new screen begins. Scroll up only when they
      // were looking INSIDE the frame - its height before a shrink that landed just ahead of this
      // message counts - so someone reading content after it (My Plan's FAQ) is left where they are.
      if (event.data.type === 'subscription:step') {
        const shrink = lastShrinkRef.current;
        const shrunkFrom = shrink && Date.now() - shrink.at < SHRINK_WINDOW_MS ? shrink.from : 0;
        const extent = Math.max(heightRef.current ?? 0, shrunkFrom);
        const { top } = frame.getBoundingClientRect();
        if (top < 0 && -top < extent) {
          window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
        return;
      }

      onMessageRef.current?.(event.data as CheckoutHostMessage);
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [path]);

  // Retire the loader once the cross-fade has finished. Keyed on `revealed` rather than driven from
  // each reveal site, so the create, the resize and the fallback below cannot each schedule one.
  useEffect(() => {
    if (!revealed) return;
    const timer = window.setTimeout(() => setLoaderMounted(false), revealDuration());
    return () => window.clearTimeout(timer);
  }, [revealed]);

  // The document has loaded but checkout has not spoken. Give it a bounded moment, then reveal
  // whatever is there rather than leaving a spinner up forever.
  const handleLoad = () => {
    window.setTimeout(() => setRevealed(true), READY_FALLBACK_MS);
  };

  const fade = `opacity ${revealDuration()}ms ease-in-out`;

  return (
    <P2GBox sx={{ position: 'relative', minHeight: revealed ? undefined : minHeight }}>
      {children}
      {loaderMounted && (
        <P2GBox
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: revealed ? 0 : 1,
            transition: fade,
            // It sits on top of the frame, so it must stop taking clicks the moment it starts
            // fading - otherwise it swallows a press on a button the customer can already see.
            pointerEvents: 'none'
          }}
          aria-hidden={revealed || undefined}
          dataTestId="checkout-frame-loading"
        >
          <Loading />
        </P2GBox>
      )}
      {/* `scrolling` is suppressed only once `checkout:resize` has told us a height: the frame is
          then sized to its content, so its own scrollbar is redundant chrome inside our page. A
          frame that never reports one (a disabled tenant, a host checkout does not recognise) keeps
          it, because clipping content with no way to reach it is worse than a scrollbar.
          `display: block` kills the inline baseline gap below the frame, which otherwise pushes our
          own page into overflow. */}
      <iframe
        src={buildSrc(path, params ?? {}, reloadNonce)}
        title={title}
        ref={iframeRef}
        scrolling={height ? 'no' : 'auto'}
        onLoad={handleLoad}
        style={{
          width: '100%',
          height: height ? `${height}px` : '100%',
          minHeight: height ? undefined : minHeight,
          border: 'none',
          display: 'block',
          // Faded rather than unmounted or `visibility: hidden`: the frame has to be in the DOM to
          // load at all, and opacity both keeps its layout box (so the loader has the right space to
          // sit in) and is the property that can actually animate - `visibility` only flips.
          opacity: revealed ? 1 : 0,
          transition: fade,
          // Nothing behind a transparent frame should be clickable or focusable on the way in.
          pointerEvents: revealed ? undefined : 'none'
        }}
        aria-hidden={revealed ? undefined : true}
      />
    </P2GBox>
  );
};

export default CheckoutFrame;
